import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  MessageCircle,
  Menu,
  X,
  Activity,
  Rocket,
  Timer,
  Radio,
  ShieldCheck,
  Eye,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/motion/Reveal";
import {
  PROJECTS,
  STATUS_META,
  ProjectCard,
  type Status,
} from "@/components/LiveActivity";
import logoAsset from "@/assets/flashweb-logo.webp.asset.json";

const WA_URL =
  "https://wa.me/41782369200?text=Ciao%20Filippo%2C%20ho%20visto%20la%20pagina%20trasparenza%20e%20vorrei%20avviare%20un%20progetto.";

const FILTERS: Array<{ id: Status | "all"; label: string }> = [
  { id: "all", label: "Tutti" },
  { id: "design", label: "In design" },
  { id: "dev", label: "In sviluppo" },
  { id: "review", label: "In revisione" },
  { id: "ready", label: "Pronto alla consegna" },
];

export const Route = createFileRoute("/trasparenza")({
  head: () => ({
    meta: [
      { title: "Trasparenza — Guarda i progetti in lavorazione in tempo reale | FlashWeb" },
      {
        name: "description",
        content:
          "Ogni sito FlashWeb viene seguito passo dopo passo. Vedi lo stato reale dei progetti attivi, senza promesse vuote.",
      },
      {
        property: "og:title",
        content: "Trasparenza — Progetti in lavorazione in tempo reale | FlashWeb",
      },
      {
        property: "og:description",
        content:
          "Nessuna promessa. Solo lavoro reale. Guarda i progetti FlashWeb attivi in questo momento.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.flash-web.ch/trasparenza" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/trasparenza" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.flash-web.ch/" },
            { "@type": "ListItem", position: 2, name: "Trasparenza", item: "https://www.flash-web.ch/trasparenza" },
          ],
        }),
      },
    ],
  }),
  component: TrasparenzaPage,
});

// ─── Navbar (matches other pages) ───
function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border/50 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2 group">
          <img
            src={logoAsset.url}
            alt="FlashWeb Agency"
            width="144"
            height="36"
            className="h-9 w-auto brightness-110 contrast-125 transition-transform group-hover:scale-105"
          />
          <span className="font-display text-lg font-bold tracking-tight">FlashWeb</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="hero" size="default" asChild className="hidden sm:inline-flex fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight />
            </a>
          </Button>
          <Button variant="hero" size="sm" asChild className="sm:hidden fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <button
            type="button"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/60 text-foreground"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/50 bg-background/95 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl flex-col px-4 py-3 text-sm">
            {[
              { href: "/", label: "Home" },
              { href: "/servizi", label: "Servizi" },
              { href: "/prezzi", label: "Prezzi" },
              { href: "/come-funziona", label: "Come funziona" },
              { href: "/portfolio", label: "Portfolio" },
              { href: "/recensioni", label: "Recensioni" },
              { href: "/trasparenza", label: "Trasparenza" },
              { href: "/chi-siamo", label: "Chi siamo" },
              { href: "/faq", label: "FAQ" },
              { href: "/blog", label: "Blog" },
              { href: "/contatti", label: "Contatti" },
              { href: "/portal", label: "Area clienti" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-foreground/90 transition-colors hover:bg-muted hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border bg-card/30">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <img src={logoAsset.url} alt="FlashWeb Agency" width="112" height="28" className="h-7 w-auto" />
              <span className="font-display text-sm font-bold">FlashWeb Agency</span>
            </div>
            <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} — Tutti i diritti riservati.</p>
            <p className="text-sm text-muted-foreground">
              Un brand di <span className="font-semibold text-foreground">FVT FuturaVision Technology GmbH</span>
            </p>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sedi</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><span className="font-semibold text-foreground">Bareerstrasse 75</span><br />6300 Zug, Svizzera</p>
              <p><span className="font-semibold text-foreground">Via Ai Prati 2</span><br />Bedano, Svizzera</p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Contatti</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><a href="mailto:info@flash-web.ch" className="font-semibold text-foreground hover:underline">info@flash-web.ch</a></p>
              <p><a href="https://wa.me/41782369200" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:underline">WhatsApp +41 78 236 92 00</a></p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Informazioni legali</h4>
            <p className="text-sm text-muted-foreground">N° registro commercio:<br /><span className="font-semibold text-foreground">CH-170.4.022.832-9</span></p>
            <div className="flex flex-col gap-1.5 pt-1 text-sm">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link>
              <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground">Cookie Policy</Link>
              <Link to="/termini-condizioni" className="text-muted-foreground hover:text-foreground">Termini e Condizioni</Link>
              <Link to="/impressum" className="text-muted-foreground hover:text-foreground">Impressum</Link>
            </div>
          </div>
        </div>
        <nav aria-label="Naviga" className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/60 pt-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <Link to="/servizi" className="hover:text-foreground">Servizi</Link>
          <Link to="/prezzi" className="hover:text-foreground">Prezzi</Link>
          <Link to="/come-funziona" className="hover:text-foreground">Come funziona</Link>
          <Link to="/portfolio" className="hover:text-foreground">Portfolio</Link>
          <Link to="/recensioni" className="hover:text-foreground">Recensioni</Link>
          <Link to="/trasparenza" className="hover:text-foreground">Trasparenza</Link>
          <Link to="/chi-siamo" className="hover:text-foreground">Chi siamo</Link>
          <Link to="/faq" className="hover:text-foreground">FAQ</Link>
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <Link to="/contatti" className="hover:text-foreground">Contatti</Link>
        </nav>
        <div className="mt-6 flex flex-col gap-2 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} FVT FuturaVision Technology GmbH — Tutti i diritti riservati.</p>
          <p>Realizzato con precisione svizzera 🇨🇭</p>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════ PAGE ═══════════════
function TrasparenzaPage() {
  const [filter, setFilter] = useState<Status | "all">("all");
  const [hydrated, setHydrated] = useState(false);
  const [now, setNow] = useState<Date | null>(null);

  useEffect(() => {
    setHydrated(true);
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return PROJECTS;
    return PROJECTS.filter((p) => p.status === filter);
  }, [filter]);

  const counts = useMemo(() => {
    const c: Record<Status, number> = { design: 0, dev: 0, review: 0, ready: 0 };
    for (const p of PROJECTS) c[p.status]++;
    return c;
  }, []);

  // Stable during SSR, updated after hydration to avoid mismatch
  const deliveredThisMonth = hydrated ? 6 + new Date().getDate() - 1 : 6;
  const nowStr = now
    ? now.toLocaleTimeString("it-CH", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : "";


  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-3xl px-6 py-20 text-center sm:py-28">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-rose-300 backdrop-blur">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
              </span>
              Trasparenza · Live
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
              Nessuna promessa. <span className="text-gradient">Solo lavoro reale.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
              Non vendiamo siti standard. Ecco cosa stiamo costruendo adesso, live.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONTROL ROOM */}
      <section className="relative border-b border-border bg-card/30">
        <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto w-full max-w-6xl px-6 py-16 sm:py-20">
          {/* Dashboard header strip */}
          <div className="overflow-hidden rounded-t-2xl border border-b-0 border-border bg-background/80 px-5 py-3 backdrop-blur">
            <div className="flex items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="flex gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
                </div>
                <span className="ml-2 font-mono">flashweb / control-room</span>
              </div>
              <div className="flex items-center gap-4 text-muted-foreground">
                <span className="hidden font-mono sm:inline">{nowStr} CET</span>
                <span className="flex items-center gap-2">
                  <Radio className="h-3.5 w-3.5 text-emerald-400 animate-pulse" />
                  <span className="font-mono">system online</span>
                </span>
              </div>
            </div>
          </div>

          {/* Legend + filters */}
          <div className="border-x border-border bg-background/40 px-4 py-4 backdrop-blur sm:px-5">
            <div className="flex flex-wrap items-center gap-2">
              {FILTERS.map((f) => {
                const active = filter === f.id;
                const meta = f.id === "all" ? null : STATUS_META[f.id];
                const count = f.id === "all" ? PROJECTS.length : counts[f.id];
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFilter(f.id)}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-all ${
                      active
                        ? meta
                          ? meta.chip
                          : "border-primary/60 bg-primary/15 text-primary"
                        : "border-border bg-background/60 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {meta && <span className={`h-1.5 w-1.5 rounded-full ${meta.dot} animate-pulse`} />}
                    <span>{f.label}</span>
                    <span className="rounded-full bg-background/60 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Projects grid — show all */}
          <div className="rounded-b-2xl border border-border bg-background/30 p-4 backdrop-blur sm:p-5">
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-border/60 bg-background/60 p-10 text-center font-mono text-sm text-muted-foreground">
                Nessun progetto in questo stato al momento.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((p) => (
                  <ProjectCard key={p.id} p={p} />
                ))}
              </div>
            )}
            <div className="mt-4 flex items-center justify-between border-t border-border/60 pt-3 font-mono text-[11px] text-muted-foreground">
              <span>{filtered.length} progetti visualizzati</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_theme(colors.emerald.400)] animate-pulse" />
                sync live
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* METRICHE DEL MESE */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <Reveal>
            <div className="mb-8 max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                Le metriche del mese
              </p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                I numeri che <span className="text-gradient">contiamo davvero</span>.
              </h2>
            </div>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-3">
            <MetricCard icon={Activity} value={PROJECTS.length} label="Siti in lavorazione" />
            <MetricCard
              icon={Rocket}
              value={deliveredThisMonth}
              label="Progetti consegnati questo mese"
            />
            <MetricCard icon={Timer} value={48} suffix="h" label="Tempo medio di consegna" />
          </div>
        </div>
      </section>

      {/* COSA SIGNIFICA TRASPARENZA */}
      <section className="border-b border-border bg-card/30">
        <div className="mx-auto max-w-4xl px-6 py-16 sm:py-20">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary backdrop-blur">
              <Eye className="h-3 w-3" />
              Cosa significa trasparenza per noi
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Mostriamo il lavoro <span className="text-gradient">mentre lo facciamo.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <Reveal delay={0.15}>
              <div className="h-full rounded-2xl border border-border bg-background/60 p-6 backdrop-blur">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">
                  Progetti reali, non solo case study curati
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  I case study nel portfolio mostrano il risultato finale, lucidato. Qui mostriamo
                  il lavoro in corso, con le percentuali reali di avanzamento e gli ETA aggiornati.
                  È il modo più onesto per farti capire come lavoriamo, prima ancora di firmare.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="h-full rounded-2xl border border-border bg-background/60 p-6 backdrop-blur">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <Lock className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">
                  Privacy dei clienti: sempre prima
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  I nomi dei progetti nella vista pubblica sono anonimizzati per categoria e area
                  (es. &ldquo;Studio Dentistico Milano&rdquo;) finché il sito non è live e il
                  cliente non acconsente esplicitamente ad apparire nel portfolio. Nessun URL,
                  nessuna anteprima, nessun dato riservato viene mostrato senza permesso scritto.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative">
        <div className="pointer-events-none absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-3xl px-6 py-20 text-center sm:py-24">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary backdrop-blur">
              <MessageCircle className="h-3 w-3" />
              Il prossimo progetto potrebbe essere il tuo
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Vuoi vedere il <span className="text-gradient">tuo nome</span> in questa control room?
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Configura il tuo sito o scrivici su WhatsApp: il tuo progetto entra in dashboard entro
              24 ore dal briefing, e da quel momento vedi ogni avanzamento in tempo reale.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="hero" size="xl" asChild className="fw-shimmer">
                <Link to="/" hash="price">
                  Configura il tuo sito <ArrowRight />
                </Link>
              </Button>
              <Button variant="outlineHero" size="xl" asChild>
                <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-5 w-5" /> Scrivi su WhatsApp
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function MetricCard({
  icon: Icon,
  value,
  suffix,
  label,
}: {
  icon: typeof Activity;
  value: number;
  suffix?: string;
  label: string;
}) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-border bg-background/60 p-6 backdrop-blur">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="font-display text-3xl font-bold tracking-tight">
          <span>{value}</span>
          {suffix && <span className="ml-1 text-xl text-muted-foreground">{suffix}</span>}
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}
